import { useState } from 'react';
import { List, useTable } from '@refinedev/antd';
import { useNavigate, useSearchParams } from 'react-router';
import type { ToolkitConfig } from '@viq-admin/types';
import {
  DataTable,
  RowActionsMenu,
  useUrlTableState,
  type DataColumn,
  type TableState,
} from '@viq-admin/ui';
import { AppliedConferencesModal } from './AppliedConferencesModal';

/**
 * Seeds the default sort when the URL carries none. Module-level so its
 * identity is stable across renders — see DataTable's `defaultState` JSDoc.
 */
const DEFAULT_STATE: Partial<TableState> = {
  sort: { field: 'name', order: 'ascend' },
};

/**
 * List of reusable toolkit configs (global templates, not tenant-scoped —
 * there is no archived list). Each row shows the config name, its
 * `toolkitType`, and how many conferences it is applied to. Opening a config
 * is the ROW CLICK (→ `/toolkits/:id`, `ToolkitDetail` — a read-first view
 * with an in-place Edit toggle); the kebab's sole action opens the
 * AppliedConferencesModal,
 * which lists the applied conferences with detach / reapply controls.
 *
 * `read Toolkit` gates the page (route-level); the modal's mutating actions
 * are gated on `manage Toolkit`. "View conferences" itself is ungated — it is
 * a read action, and the page-level gate already covers it.
 */
export function ToolkitsList() {
  const { tableProps } = useTable<ToolkitConfig>({
    resource: 'toolkits',
    // DataTable owns paging; Refine must not slice the payload.
    pagination: { mode: 'off' },
  });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useUrlTableState(searchParams, setSearchParams);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const columns: DataColumn<ToolkitConfig>[] = [
    { dataIndex: 'name', key: 'name', title: 'Name', kind: 'text', width: 200 },
    {
      dataIndex: 'toolkitType',
      key: 'toolkitType',
      title: 'Type',
      kind: 'enum',
      width: 150,
    },
    {
      dataIndex: 'appliedConferenceCount',
      key: 'appliedConferenceCount',
      title: 'Applied to',
      kind: 'number',
      width: 120,
    },
    {
      key: 'actions',
      title: '',
      kind: 'actions',
      render: (_: unknown, record: ToolkitConfig) => (
        <RowActionsMenu
          actions={[
            {
              key: 'view-conferences',
              label: 'View conferences',
              onRun: () => setSelectedId(record.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <List>
        <DataTable<ToolkitConfig>
          tableProps={tableProps}
          columns={columns}
          rowKey="id"
          storageKey="toolkitsList"
          state={state}
          onStateChange={setState}
          defaultState={DEFAULT_STATE}
          searchFields={['name']}
          searchPlaceholder="Search toolkits"
          onRowClick={(record) => navigate(`/toolkits/${record.id}`)}
          empty={{
            title: 'No toolkits yet',
            description: 'Create the first toolkit config to get started.',
          }}
        />
      </List>
      {selectedId && (
        <AppliedConferencesModal
          configId={selectedId}
          open
          onClose={() => setSelectedId(null)}
        />
      )}
    </>
  );
}
